import type { CredentialSummary } from "@/components/CredentialFields";
import type { Tag } from "@/components/TagsEditor";

export const CONNECTION_METHODS_BY_KIND: Record<string, string[]> = {
  Linux: ["SSH"],
  Windows: ["RDP"],
  Postgres: ["PostgresWireProtocol"],
  MSSQL: ["TDS"],
  MySQL: ["MySQLProtocol"],
  Generic: ["KubernetesAPI"],
  EKS: ["KubernetesAPI"],
  AKS: ["KubernetesAPI"],
  GKE: ["KubernetesAPI"],
};

export function isManagedKubernetes(kind: string): boolean {
  return kind === "EKS" || kind === "AKS" || kind === "GKE";
}

const ADDRESS_LABELS_BY_KIND: Record<string, string> = {
  EKS: "Endpoint",
};

export function addressLabelFor(kind: string): string {
  return ADDRESS_LABELS_BY_KIND[kind] ?? "Address";
}

export const DEFAULT_PORT_BY_CONNECTION_METHOD: Record<string, number> = {
  SSH: 22,
  RDP: 3389,
  PostgresWireProtocol: 5432,
  TDS: 1433,
  MySQLProtocol: 3306,
  KubernetesAPI: 443,
};

export const CONNECTION_METHOD_LABELS: Record<string, string> = {
  SSH: "SSH",
  RDP: "RDP",
  PostgresWireProtocol: "Postgres wire protocol",
  TDS: "TDS",
  MySQLProtocol: "MySQL protocol",
  KubernetesAPI: "Kubernetes API",
};

export const CREDENTIAL_TYPES_BY_CONNECTION_METHOD: Record<string, string[]> = {
  SSH: ["UsernamePassword", "UsernameKeyPair"],
  RDP: ["UsernamePassword", "DomainUsernamePassword"],
  TDS: ["UsernamePassword"],
  PostgresWireProtocol: ["UsernamePassword"],
  MySQLProtocol: ["UsernamePassword"],
  KubernetesAPI: [
    "AwsIamRole",
    "AwsAccessKey",
    "AzureManagedIdentity",
    "AzureServicePrincipal",
    "GoogleIdentity",
    "GoogleServiceAccount",
    "BearerToken",
  ],
};

const KIND_BY_CREDENTIAL_TYPE: Record<string, string> = {
  AwsAccessKey: "EKS",
  AwsIamRole: "EKS",
  AzureServicePrincipal: "AKS",
  AzureManagedIdentity: "AKS",
  GoogleServiceAccount: "GKE",
  GoogleIdentity: "GKE",
};

export function usesOwnIdentity(credentialType: string) {
  return (
    credentialType === "AwsIamRole" ||
    credentialType === "AzureManagedIdentity" ||
    credentialType === "GoogleIdentity"
  );
}

const EKS_ENDPOINT =
  /\.([a-z]{2}(?:-[a-z]+)+-\d+)\.(?:eks\.amazonaws\.com(?:\.cn)?|api\.aws|api\.amazonwebservices\.com\.cn)$/i;

const GKE_DNS_ENDPOINT = /^([a-z0-9-]+\.)+gke\.goog$/i;

function hostOf(address: string): string {
  return address
    .trim()
    .replace(/^https:\/\//i, "")
    .replace(/\/+$/, "")
    .replace(/:443$/, "");
}

export function eksRegion(address: string): string | null {
  return EKS_ENDPOINT.exec(hostOf(address))?.[1]?.toLowerCase() ?? null;
}

export function usesPublicCertificate(kind: string, address: string): boolean {
  return kind === "GKE" && GKE_DNS_ENDPOINT.test(hostOf(address));
}

export function credentialTypesForKind(kind: string): string[] {
  const method = CONNECTION_METHODS_BY_KIND[kind]?.[0] ?? "";

  return (CREDENTIAL_TYPES_BY_CONNECTION_METHOD[method] ?? []).filter(
    (type) => (KIND_BY_CREDENTIAL_TYPE[type] ?? kind) === kind,
  );
}

// Mirrors ResourceValidator.CredentialInjectingConnectionMethods. Only these
// protocols are terminated by the gateway, so only these can have a credential
// injected - the rest are forwarded byte for byte.
export const CREDENTIAL_INJECTING_CONNECTION_METHODS = [
  "SSH",
  "RDP",
  "KubernetesAPI",
];

export const STATUS_OPTIONS = ["Online", "Offline"];

export interface ResourceCreator {
  userId: string;
  name: string;
  email: string;
}

export interface ResourceTeamGrant {
  teamId: string;
  teamName: string;
}

export interface ConnectorAssignment {
  connectorId: string;
  connectorName: string;
  reachable: boolean | null;
  lastCheckedAt: string | null;
  online: boolean;
}

export interface GatewayAssignment {
  assignmentId: string;
  gatewayId: string;
  gatewayName: string;
  gatewaySource: string;
  reachable: boolean | null;
  lastCheckedAt: string | null;
  online: boolean;
}

export interface Resource {
  id: string;
  name: string;
  description: string | null;
  type: string;
  kind: string | null;
  connectionMethod: string | null;
  address: string;
  port: number;
  clusterName: string | null;
  createdBy: ResourceCreator;
  createdAt: string;
  teams: ResourceTeamGrant[];
  connectors: ConnectorAssignment[];
  gateways: GatewayAssignment[];
  tags: Tag[];
  credential: CredentialSummary | null;

  /** "Verified", "AwaitingApproval", or null where there is nothing to say. */
  hostIdentityState: string | null;

  /** Decided by the server: "Reachable", "Unreachable", "NotChecked", "NoRoute". */
  routeState: RouteState;

  routes: ResourceRoute[];
}

export interface ResourceRoute {
  gatewayName: string | null;
  connectorName: string | null;
  /** "Reachable", "Unreachable", "NotChecked", or "Unknown" while offline. */
  state: string;
  lastCheckedAt: string | null;
}

export type MemberResource = Omit<
  Resource,
  "credential" | "createdBy" | "createdAt" | "clusterName"
>;

export interface ConnectorOption {
  id: string;
  name: string;
}

export interface GatewayOption {
  id: string;
  name: string;
  source: string;
}

/** Mirrors ResourceRouteStates on the server, which decides the state. */
export type RouteState = "Reachable" | "Unreachable" | "NotChecked" | "NoRoute";

const ROUTE_STATES: RouteState[] = [
  "Reachable",
  "Unreachable",
  "NotChecked",
  "NoRoute",
];

export function isRouteState(value: string): value is RouteState {
  return (ROUTE_STATES as string[]).includes(value);
}

export function needsAttention(resource: Resource): boolean {
  return (
    resource.routeState !== "Reachable" ||
    resource.hostIdentityState === "AwaitingApproval"
  );
}
